import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Settings, 
  Eye, 
  Share2, 
  Save, 
  Palette,
  Layout,
  Plus,
  Monitor,
  Sparkles,
  Smartphone,
  Tablet,
  CreditCard,
  AlertCircle,
  Menu,
  X
} from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { COLOR_THEMES } from '../constants/colorThemes';
import LivePreview from './LivePreview';
import SectionEditor from './SectionEditor';
import LanguageManager from './LanguageManager';
import TranslationManager from './TranslationManager';
import PublishingModal from './PublishingModal';
import CustomThemeCreator from './CustomThemeCreator';
import TransferRestrictionBanner from './TransferRestrictionBanner';
import DomainManager from './DomainManager';
import { getPreviewUrl } from '../utils/siteUrls';
import toast from 'react-hot-toast';

// Import font options from onboarding
const FONT_OPTIONS = [
  { name: 'Inter', label: 'Modern' },
  { name: 'Urbanist', label: 'Bold' },
  { name: 'Poppins', label: 'Friendly' },
  { name: 'Playfair Display', label: 'Elegant' },
  { name: 'Roboto', label: 'Clean' },
  { name: 'Montserrat', label: 'Strong' },
  { name: 'Open Sans', label: 'Classic' },
  { name: 'Lato', label: 'Refined' },
  { name: 'Source Sans Pro', label: 'Professional' },
  { name: 'Nunito', label: 'Rounded' },
];




const SiteEditor = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { currentSite, fetchSite, updateSite, publishSite, setCurrentSite } = useSite();
  const [activePanel, setActivePanel] = useState('sections');
  const [selectedSection, setSelectedSection] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [regeneratingSection, setRegeneratingSection] = useState(null);
  const [regenerationStep, setRegenerationStep] = useState(0);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [showSectionChooser, setShowSectionChooser] = useState(false);
  const [addingSection, setAddingSection] = useState(null);
  const [addingSectionStep, setAddingSectionStep] = useState(0);
  const [showPublishingModal, setShowPublishingModal] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  
  // NEW: Multi-page state management
  const [currentPage, setCurrentPage] = useState(null);
  const [showPageManager, setShowPageManager] = useState(false);
  const [showNavigationEditor, setShowNavigationEditor] = useState(false);
  const [addingPage, setAddingPage] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [confirmationModal, setConfirmationModal] = useState(null);
  
  // Mobile drawer state
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Translation manager state
  const [showTranslationManager, setShowTranslationManager] = useState(false);
  
  // Theme management state
  const [showCustomThemeCreator, setShowCustomThemeCreator] = useState(false);
  const [editingCustomTheme, setEditingCustomTheme] = useState(null);

  // Domain management state
  const [showDomainManager, setShowDomainManager] = useState(false);

  const saveTimeoutRef = useRef(null);

  // Available section types for the section chooser (only AI-supported types)
  const AVAILABLE_SECTIONS = [
    { type: 'hero', name: 'Hero', description: 'Main banner with title and call to action' },
    { type: 'faq', name: 'FAQ', description: 'Frequently asked questions and answers' },
    { type: 'services', name: 'Services', description: 'Products or services offered' },
    { type: 'features', name: 'Features', description: 'Key benefits and capabilities' },
    { type: 'team', name: 'Team', description: 'Team members and staff' },
    { type: 'gallery', name: 'Gallery', description: 'Photo gallery or portfolio' },
    { type: 'testimonials', name: 'Testimonials', description: 'Customer reviews and feedback' },
    { type: 'pricing', name: 'Pricing', description: 'Pricing plans and packages' },
    { type: 'article', name: 'Article', description: 'Article or blog post content' },
    { type: 'cta', name: 'Call to Action', description: 'Drive conversions and engagement' },
    { type: 'contact', name: 'Contact', description: 'Contact information and form' },
    { type: 'newsletter', name: 'Newsletter', description: 'Newsletter signup section' },
    { type: 'iframe', name: 'Embedded Content', description: 'Embed external content like videos, maps, or widgets' },
    { type: 'banner', name: 'Banner', description: 'Full-width banner with text and background image' },
    { type: 'custom-code', name: 'Custom Code', description: 'Add custom HTML, CSS, and JavaScript code' }
  ];

  // Get current design object (page-specific or site-wide)
  const getCurrentDesign = () => {
    if (currentSite?.isMultiPage && currentPage && !currentPage.isDefault) {
      // Check if custom theme is enabled for this page
      if (currentPage.design?.customThemeEnabled !== false) {
        // Return page-specific design, falling back to site design for missing properties
        return {
          ...currentSite.design,
          ...currentPage.design
        };
      }
    }
    // For main page, single-page sites, or pages with custom theme disabled
    return currentSite?.design || {};
  };

  // Get current theme or default (page-specific or site-wide)
  const getCurrentTheme = () => {
    const design = getCurrentDesign();

    if (design?.colorTheme) {
      // If colorTheme has colors property, it's the full theme object
      if (design.colorTheme.colors) {
        return design.colorTheme;
      }
      // If colorTheme has primary property, it's the database format - convert to full theme
      if (design.colorTheme.primary) {
        const themeName = design.colorThemeName || 'Custom';
        return {
          name: themeName,
          colors: design.colorTheme
        };
      }
    }
    return COLOR_THEMES[0]; // Default to Coastal Blue
  };

  // NEW: Multi-page helper functions
  const getCurrentPages = () => {
    if (currentSite?.isMultiPage && currentSite?.pages) {
      return currentSite.pages.sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return [];
  };

  const getCurrentPageSections = () => {
    if (currentPage && currentPage.sections) {
      return currentPage.sections;
    }
    // Fallback to legacy sections
    return currentSite?.sections || [];
  };

  const getDefaultPage = () => {
    const pages = getCurrentPages();
    return pages.find(p => p.isDefault) || pages[0] || null;
  };

  // Check if multi-page UI should be shown
  const shouldShowMultiPageUI = currentSite?.isMultiPage || getCurrentPages().length > 0;

  // Check if current page is the main/default page
  const isMainPage = () => {
    if (!currentSite?.isMultiPage) return true; // Single-page sites are always main
    return currentPage?.isDefault || false;
  };

  // Check if header/footer editing should be allowed
  const canEditHeaderFooter = () => {
    return isMainPage();
  };

  const generatePageSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50) || 'page';
  };

  const handlePageSwitch = (pageId) => {
    const page = getCurrentPages().find(p => p.id === pageId);
    if (page) {
      setCurrentPage(page);
      setSelectedSection(null);
      setActivePanel('sections');
    }
  };

  const handlePageAdd = async (pageName) => {
    if (!currentSite || !pageName.trim()) return;

    try {
      setAddingPage(true);

      const pageSlug = generatePageSlug(pageName);
      
      // Check if slug already exists
      const existingPage = getCurrentPages().find(p => p.slug === pageSlug);
      if (existingPage) {
        toast.error('A page with this name already exists');
        return;
      }

      const newPageId = `page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // NOTE: Header and footer sections are NOT copied to sub pages
      // They are automatically inherited from the main page during HTML generation
      // This prevents duplicate headers and ensures consistent branding across all pages
      const inheritedSections = [];
      
      // Clone the main page's design for the new page
      const mainPageDesign = currentSite.design || {};
      
      const newPage = {
        id: newPageId,
        name: pageName.trim(),
        slug: pageSlug,
        isDefault: false,
        sections: inheritedSections,
        design: {
          // Clone all design properties from the main page
          ...mainPageDesign,
          // Explicitly enable custom theme for new pages
          customThemeEnabled: true
        },
        seo: {
          title: `${pageName} | ${currentSite.businessInfo?.name || currentSite.name}`,
          description: '',
          keywords: []
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add page to site
      const updatedPages = [...getCurrentPages(), newPage];
      const updatedSite = {
        ...currentSite,
        pages: updatedPages,
        isMultiPage: true
      };

      setCurrentSite(updatedSite);
      
      // Force immediate save for page creation (don't use debounced save)
      console.log('🔄 Force saving new page immediately...');
      try {
        await updateSite(currentSite._id, updatedSite);
        console.log('✅ New page saved successfully:', pageName);
        setUnsavedChanges(false);
      } catch (error) {
        console.error('❌ Failed to save new page:', error);
        toast.error('Failed to save new page');
        setUnsavedChanges(true);
        return;
      }

      // Select the new page
      setCurrentPage(newPage);
      setNewPageName('');
      setShowPageManager(false);

      toast.success(`✨ Page "${pageName}" created!`);
    } catch (error) {
      console.error('Failed to add page:', error);
      toast.error('Failed to create page');
    } finally {
      setAddingPage(false);
    }
  };

  const handlePageDelete = async (pageId) => {
    if (!currentSite) return;

    const pageToDelete = getCurrentPages().find(p => p.id === pageId);
    if (!pageToDelete) return;

    if (pageToDelete.isDefault) {
      toast.error('Cannot delete the default page');
      return;
    }

    try {
      const updatedPages = getCurrentPages().filter(p => p.id !== pageId);
      const updatedSite = {
        ...currentSite,
        pages: updatedPages
      };

      setCurrentSite(updatedSite);
      setUnsavedChanges(true);
      debouncedSave(updatedSite);

      // Select default page if current page was deleted
      if (currentPage?.id === pageId) {
        const defaultPage = getDefaultPage();
        setCurrentPage(defaultPage);
      }

      toast.success(`Page "${pageToDelete.name}" deleted`);
    } catch (error) {
      console.error('Failed to delete page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handlePageUpdate = (pageId, updates) => {
    if (!currentSite) return;

    const updatedPages = getCurrentPages().map(page =>
      page.id === pageId ? { ...page, ...updates, updatedAt: new Date() } : page
    );

    const updatedSite = { ...currentSite, pages: updatedPages };
    setCurrentSite(updatedSite);
    setUnsavedChanges(true);
    debouncedSave(updatedSite);

    // Update current page if it was modified
    if (currentPage?.id === pageId) {
      const updatedPage = updatedPages.find(p => p.id === pageId);
      setCurrentPage(updatedPage);
    }
  };

  // Function to ensure a page has header and footer sections
  const ensurePageHasHeaderFooter = (page) => {
    if (!page || !page.sections) return page;
    
    const hasHeader = page.sections.some(s => s.type === 'header');
    const hasFooter = page.sections.some(s => s.type === 'footer');
    
    if (hasHeader && hasFooter) return page; // Already has both
    
    // Find source sections for header/footer
    let sourceSections = null;
    const mainPage = getDefaultPage();
    if (mainPage && mainPage.sections && mainPage.sections.length > 0) {
      sourceSections = mainPage.sections;
    }
    
    if (!sourceSections) return page;
    
    const updatedSections = [...page.sections];
    
    // Add header if missing
    if (!hasHeader) {
      const headerSection = sourceSections.find(s => s.type === 'header');
      if (headerSection) {
        const newHeader = {
          ...headerSection,
          id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        updatedSections.unshift(newHeader); // Add to beginning
      }
    }
    
    // Add footer if missing
    if (!hasFooter) {
      const footerSection = sourceSections.find(s => s.type === 'footer');
      if (footerSection) {
        const newFooter = {
          ...footerSection,
          id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        updatedSections.push(newFooter); // Add to end
      }
    }
    
    return { ...page, sections: updatedSections };
  };

  // Function to fix all pages missing header/footer
  const fixAllPagesSections = () => {
    if (!currentSite) return;
    
    if (!currentSite.isMultiPage) {
      toast.error('This is not a multi-page site');
      return;
    }
    
    const pages = getCurrentPages();
    console.log('=== DIAGNOSING HEADER/FOOTER SECTIONS ===');
    // Legacy sections removed - only using page sections
    console.log('Main page sections:', getDefaultPage()?.sections?.map(s => s.type) || 'none');
    console.log('All pages:', pages.map(p => ({ name: p.name, sections: p.sections?.map(s => s.type) || 'none' })));
    
    let needsUpdate = false;
    const updatedPages = pages.map(page => {
      const originalSectionCount = page.sections?.length || 0;
      const fixedPage = ensurePageHasHeaderFooter(page);
      const newSectionCount = fixedPage.sections?.length || 0;
      
      if (newSectionCount !== originalSectionCount) {
        needsUpdate = true;
        console.log(`Fixed header/footer for page: ${page.name} (${originalSectionCount} -> ${newSectionCount} sections)`);
      }
      return fixedPage;
    });
    
    if (needsUpdate) {
      const updatedSite = { ...currentSite, pages: updatedPages };
      setCurrentSite(updatedSite);
      setUnsavedChanges(true);
      debouncedSave(updatedSite);
      
      // Update current page if it was modified
      if (currentPage) {
        const updatedCurrentPage = updatedPages.find(p => p.id === currentPage.id);
        if (updatedCurrentPage) {
          setCurrentPage(updatedCurrentPage);
        }
      }
      
      toast.success('Fixed missing header/footer sections on all pages');
    } else {
      console.log('No pages needed fixing, but checking if we have source sections...');
      
      // Check if we actually have source sections to copy from
      const mainPage = getDefaultPage();
      const hasSourceSections = mainPage?.sections && mainPage.sections.length > 0;
      
      if (!hasSourceSections) {
        toast.error('No header/footer sections found to copy from. Please create header/footer on the main page first.');
      } else {
        toast.info('All pages already have header/footer sections');
      }
    }
  };

  // Initialize current page when site loads
  useEffect(() => {
    if (currentSite && !currentPage) {
      if (currentSite.isMultiPage && currentSite.pages?.length > 0) {
        const defaultPage = getDefaultPage();
        setCurrentPage(defaultPage);
        
        // Auto-fix pages missing header/footer sections (only once per session)
        const hasAutoFixed = sessionStorage.getItem(`autofix_${currentSite._id}`);
        if (!hasAutoFixed) {
          setTimeout(() => {
            const pages = getCurrentPages();
            const pagesNeedingFix = pages.filter(page => {
              const hasHeader = page.sections?.some(s => s.type === 'header');
              const hasFooter = page.sections?.some(s => s.type === 'footer');
              return !hasHeader || !hasFooter;
            });
            
            if (pagesNeedingFix.length > 0) {
              console.log(`Auto-fixing ${pagesNeedingFix.length} pages missing header/footer:`, pagesNeedingFix.map(p => p.name));
              fixAllPagesSections();
              // Mark as auto-fixed for this session
              sessionStorage.setItem(`autofix_${currentSite._id}`, 'true');
            }
          }, 1000); // Delay to ensure everything is loaded
        }
      } else {
        // For legacy single-page sites, create a virtual page object
        setCurrentPage({
          id: 'legacy',
          name: 'Home',
          slug: 'index',
          isDefault: true,
          sections: [],
          design: {},
          seo: currentSite.seo || {}
        });
      }
    }
  }, [currentSite]);

  useEffect(() => {
    if (siteId) {
      setLoading(true);
      // Always clear currentSite when siteId changes to prevent showing stale data
      setCurrentSite(null);
      
      // Reset editor state to prevent showing data from previous site
      setSelectedSection(null);
      setCurrentPage(null);
      setActivePanel('sections');
      setShowEditModal(null);
      setShowColorPicker(false);
      setShowFontPicker(false);
      setShowSectionChooser(false);
      setShowPageManager(false);
      setShowNavigationEditor(false);
      setRegeneratingSection(null);
      setAddingSection(null);
      setUnsavedChanges(false);
      
      // Check if we're coming from onboarding - if so, add extra delay for backend processing
      const isFromOnboarding = sessionStorage.getItem(`fromOnboarding_${siteId}`);
      const initialDelay = isFromOnboarding ? 1000 : 0;
      
      if (isFromOnboarding) {
        console.log('🔄 Coming from onboarding, adding delay for backend processing...');
      }
      
      setTimeout(() => {
        // Load both site data and plan info
        Promise.all([
          fetchSite(siteId),
          fetch('/api/sites', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }).then(res => res.ok ? res.json() : null)
        ])
          .then(([site, sitesData]) => {
            
            // Additional check to verify site data integrity
            if (siteId !== site._id) {
              console.error('🚨 MISMATCH: Requested site ID does not match fetched site ID!', {
                requested: siteId,
                fetched: site._id,
                fetchedName: site.name
              });
            }
            
            // Set plan info if available
            if (sitesData && sitesData.planInfo) {
              setPlanInfo(sitesData.planInfo);
            }
            
            setLoading(false);
          })
          .catch(() => {
            toast.error('Site not found');
            navigate('/');
          });
      }, initialDelay);
    }
  }, [siteId, fetchSite, navigate, setCurrentSite]);

  // Handle special cases: onboarding and staleness checks
  // The main site loading is handled by the first useEffect
  useEffect(() => {
    if (siteId && currentSite && currentSite._id === siteId) {
      // Check if we're coming from the onboarding form (freshly created site)
      const isFromOnboarding = sessionStorage.getItem(`fromOnboarding_${siteId}`);
      
      if (isFromOnboarding) {
        console.log('🔄 Additional refresh needed from onboarding...');
        const onboardingComplete = sessionStorage.getItem(`onboardingComplete_${siteId}`);
        console.log('⏰ Onboarding completed at:', onboardingComplete ? new Date(parseInt(onboardingComplete)) : 'unknown');
        
        // Force a fresh fetch for onboarding sites to get latest generated content
        fetchSite(siteId).then((freshSite) => {
          console.log('✅ Fresh site data loaded from onboarding:', freshSite.name);
          console.log('📊 Sections in fresh site:', freshSite.sections?.length || 0);
        }).catch((error) => {
          console.error('❌ Failed to fetch fresh site data:', error);
        });
        sessionStorage.setItem(`lastRefresh_${siteId}`, Date.now().toString());
        sessionStorage.removeItem(`fromOnboarding_${siteId}`);
        sessionStorage.removeItem(`onboardingComplete_${siteId}`);
        return; // Skip other checks if coming from onboarding
      }
      
      // Check if we need to refresh data (in case it changed elsewhere)
      const timeSinceUpdate = Date.now() - new Date(currentSite.updatedAt).getTime();
      const lastRefresh = sessionStorage.getItem(`lastRefresh_${siteId}`);
      const timeSinceLastRefresh = lastRefresh ? Date.now() - parseInt(lastRefresh) : Infinity;
      
      // Refresh if data is stale and haven't refreshed recently
      if (timeSinceUpdate > 30000 && timeSinceLastRefresh > 10000) {
        console.log('🔄 Refreshing stale site data...');
        fetchSite(siteId);
        sessionStorage.setItem(`lastRefresh_${siteId}`, Date.now().toString());
      }
    }
  }, [siteId, currentSite, fetchSite]);

  // Set loading state based on currentSite availability
  useEffect(() => {
    if (currentSite) {
      setLoading(false);
      // Debug log to see what site is being rendered
    } else if (siteId) {
      // If we have a siteId but no currentSite, we should be loading
      setLoading(true);
    }
  }, [currentSite, siteId]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Mobile detection and drawer management
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Note: Removed auto-close on activePanel change to allow tab switching within drawer

  // Close drawer when clicking outside on mobile
  const handleDrawerBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      setIsMobileDrawerOpen(false);
    }
  }, []);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (isMobile && isMobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isMobile, isMobileDrawerOpen]);

  const handleSave = async () => {
    if (!currentSite || !unsavedChanges) return;

    setSaving(true);
    try {
      await updateSite(currentSite._id, currentSite);
      setUnsavedChanges(false);
      toast.success('Changes saved');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!currentSite) return;
    setShowPublishingModal(true);
  };

  const performPublish = async () => {
    // Force save any pending changes before publishing
    if (unsavedChanges) {
      console.log('🔄 Force saving before publish...');
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      setSaving(true);
      try {
        await updateSite(currentSite._id, currentSite);
        setUnsavedChanges(false);
        console.log('✅ Forced save completed before publish');
      } catch (error) {
        console.error('❌ Failed to save before publish:', error);
        setSaving(false);
        // Re-throw the original error to preserve response data (e.g., security threats)
        throw error;
      } finally {
        setSaving(false);
      }
    }
    
    console.log('📤 Publishing site with pages:', currentSite.pages?.map(p => p.name) || 'none');
    const result = await publishSite(currentSite._id);
    return result;
  };

  // Debounced save function with better error handling and synchronization
  const debouncedSave = useCallback((siteData) => {
    if (!currentSite?._id) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        console.log('🔄 Auto-saving site data...', { 
          pages: siteData.pages?.length || 0,
          multiPage: siteData.isMultiPage 
        });
        
        const savedSite = await updateSite(currentSite._id, siteData);
        setUnsavedChanges(false);
        
        // Ensure local state matches database state
        if (savedSite && savedSite.pages) {
          console.log('✅ Auto-save successful, pages in DB:', savedSite.pages.map(p => p.name));
        }
      } catch (error) {
        console.error('❌ Auto-save failed:', error);

        // Check if this is a security/malware detection error
        const errorData = error.response?.data;
        if (errorData?.threats && errorData.threats.length > 0) {
          const threatNames = errorData.threats.map(t => t.name || t).join(', ');
          toast.error(
            `Security issue detected: ${threatNames}. Please remove the malicious code from your custom code section.`,
            { duration: 8000 }
          );
        } else if (errorData?.error) {
          toast.error(`Save failed: ${errorData.error}`);
        } else {
          toast.error('Auto-save failed. Please save manually.');
        }
      } finally {
        setSaving(false);
      }
    }, 1000); // Save after 1 second of inactivity
  }, [currentSite?._id, updateSite]);

  const handleSectionUpdate = (sectionId, updates) => {
    if (!currentSite || !currentPage) return;

    // Update section in current page
    const updatedSections = currentPage.sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    );

    const updatedPage = { ...currentPage, sections: updatedSections, updatedAt: new Date() };
    handlePageUpdate(currentPage.id, { sections: updatedSections });
    setCurrentPage(updatedPage);
  };

  const handleSectionMove = (sectionId, direction) => {
    if (!currentSite || !currentPage) return;

    const currentSections = getCurrentPageSections();
    const sections = [...currentSections];
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    
    if (sectionIndex === -1) return;

    const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
    
    // Check bounds and prevent moving header/footer
    if (newIndex < 0 || newIndex >= sections.length) return;
    if (sections[sectionIndex].type === 'header' || sections[sectionIndex].type === 'footer') return;
    if (sections[newIndex].type === 'header' || sections[newIndex].type === 'footer') return;

    // Swap sections
    [sections[sectionIndex], sections[newIndex]] = [sections[newIndex], sections[sectionIndex]];
    
    // Update order values
    sections.forEach((section, index) => {
      section.order = index;
    });

    if (currentSite.isMultiPage) {
      handlePageUpdate(currentPage.id, { sections });
      setCurrentPage({ ...currentPage, sections });
    } else {
      const updatedSite = { ...currentSite, sections };
      setCurrentSite(updatedSite);
      setUnsavedChanges(true);
      debouncedSave(updatedSite);
    }
    
    toast.success(`Section moved ${direction}`);
  };

  const handleSectionDelete = (sectionId) => {
    if (!currentSite || !currentPage) return;

    const currentSections = getCurrentPageSections();
    const sections = currentSections.filter(s => s.id !== sectionId);
    
    // Update order values
    sections.forEach((section, index) => {
      section.order = index;
    });

    if (currentSite.isMultiPage) {
      handlePageUpdate(currentPage.id, { sections });
      setCurrentPage({ ...currentPage, sections });
    } else {
      const updatedSite = { ...currentSite, sections };
      setCurrentSite(updatedSite);
      setUnsavedChanges(true);
      debouncedSave(updatedSite);
    }
    
    // Clear selected section if it was deleted
    if (selectedSection?.id === sectionId) {
      handleSectionSelect(null);
    }
    
    toast.success('Section deleted');
  };

  const handleSectionRegenerate = async (sectionId) => {
    if (!currentSite) return;

    const section = currentPage.sections.find(s => s.id === sectionId);
    if (!section) return;

    try {
      // Set regenerating state
      setRegeneratingSection(sectionId);
      setRegenerationStep(0);

      // Enhanced loading steps with fancy messages
      const steps = [
        'Analyzing your business concept...',
        'Consulting our AI design experts...',
        'Crafting compelling content...',
        'Sourcing professional imagery...',
        'Optimizing visual hierarchy...',
        'Finalizing your masterpiece...'
      ];

      // Start step progression
      const stepInterval = setInterval(() => {
        setRegenerationStep(prev => {
          if (prev < steps.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 800);

      const response = await fetch('/api/ai/generate-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          type: section.type,
          businessInfo: currentSite.businessInfo,
          existingContent: section.content
        })
      });

      // Clear interval
      clearInterval(stepInterval);

      if (!response.ok) {
        throw new Error('Failed to regenerate content');
      }

      const result = await response.json();
      
      // Update section with new content
      handleSectionUpdate(sectionId, { content: result.content });
      
      // Reset regenerating state
      setRegeneratingSection(null);
      setRegenerationStep(0);
      
      toast.success('🎨 Your section has been beautifully reimagined!', { 
        duration: 4000,
        style: {
          background: '#10B981',
          color: 'white',
        }
      });
    } catch (error) {
      console.error('Regeneration failed:', error);
      setRegeneratingSection(null);
      setRegenerationStep(0);
      toast.error('✨ Magic failed this time. Please try again!', {
        style: {
          background: '#EF4444',
          color: 'white',
        }
      });
    }
  };

  const handleSectionAdd = async (sectionType) => {
    if (!currentSite || !currentPage) return;

    try {
      // Set adding state and close section chooser
      setAddingSection(sectionType);
      setAddingSectionStep(0);
      setShowSectionChooser(false);

      // Enhanced loading steps with section-specific messages
      const steps = [
        `Analyzing your ${sectionType} requirements...`,
        'Consulting our AI design experts...',
        'Crafting compelling content...',
        'Sourcing professional imagery...',
        'Optimizing layout and structure...',
        'Finalizing your new section...'
      ];

      // Start step progression
      const stepInterval = setInterval(() => {
        setAddingSectionStep(prev => {
          if (prev < steps.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 800);

      // Find footer index to insert before it (use current page sections)
      const currentSections = getCurrentPageSections();
      const footerIndex = currentSections.findIndex(s => s.type === 'footer');
      const insertIndex = footerIndex !== -1 ? footerIndex : currentSections.length;

      // Generate new section ID
      const newSectionId = `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      let result;
      
      // Handle sections that don't need AI generation
      if (sectionType === 'iframe' || sectionType === 'banner' || sectionType === 'custom-code') {
        // Clear interval
        clearInterval(stepInterval);
        
        if (sectionType === 'iframe') {
          // Create iframe section with default content
          result = {
            content: {
              title: 'Embedded Content',
              src: '',
              width: '100%',
              height: '400px',
              allowFullscreen: true,
              responsive: true,
              border: 'none',
              loading: 'lazy'
            }
          };
        } else if (sectionType === 'banner') {
          // Create banner section with default content
          result = {
            content: {
              text: 'Welcome to Our Business',
              height: 'medium',
              textSize: 4,
              textColor: '#ffffff',
              textShadow: true,
              backgroundImage: false,
              overlayColor: '#000000',
              overlayOpacity: 50
            }
          };
        } else if (sectionType === 'custom-code') {
          // Create custom code section with empty defaults
          result = {
            content: {
              title: '',
              description: '',
              htmlCode: '',
              cssCode: '',
              jsCode: '',
              isVisible: true,
              enabled: true,
              cssScope: 'global',
              jsExecution: 'immediate'
            }
          };
        }
      } else {
        // Generate content for AI-supported sections
        const response = await fetch('/api/ai/generate-content', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            type: sectionType,
            businessInfo: {
              ...currentSite.businessInfo,
              siteId: currentSite._id
            }
          })
        });

        // Clear interval
        clearInterval(stepInterval);

        if (!response.ok) {
          throw new Error('Failed to generate section content');
        }

        result = await response.json();
      }

      // Create new section
      const newSection = {
        id: newSectionId,
        type: sectionType,
        content: result.content,
        visible: true,
        order: insertIndex
      };

      // Insert new section into current page
      const updatedSections = [...currentSections];
      updatedSections.splice(insertIndex, 0, newSection);

      // Update order values for all sections after the inserted one
      updatedSections.forEach((section, index) => {
        section.order = index;
      });

      if (currentSite.isMultiPage) {
        handlePageUpdate(currentPage.id, { sections: updatedSections });
        setCurrentPage({ ...currentPage, sections: updatedSections });
      } else {
        const updatedSite = { ...currentSite, sections: updatedSections };
        setCurrentSite(updatedSite);
        setUnsavedChanges(true);
        debouncedSave(updatedSite);
      }

      // Reset adding state
      setAddingSection(null);
      setAddingSectionStep(0);

      // Select the new section after a brief delay
      setTimeout(() => {
        handleSectionSelect(newSection);
      }, 100);

      toast.success(`✨ ${sectionType.charAt(0).toUpperCase() + sectionType.slice(1)} section added!`, { 
        duration: 4000,
        style: {
          background: '#10B981',
          color: 'white',
        }
      });
    } catch (error) {
      console.error('Failed to add section:', error);
      setAddingSection(null);
      setAddingSectionStep(0);
      toast.error('Failed to add section. Please try again.', {
        style: {
          background: '#EF4444',
          color: 'white',
        }
      });
    }
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    // Automatically switch to sections tab when a section is selected
    if (section) {
      setActivePanel('sections');
    }
  };

  const handleThemeUpdate = (updates) => {
    if (!currentSite) return;

    // Separate settings, seo, and design updates
    const { settings, seo, ...designUpdates } = updates;

    // Process color theme updates to match database schema
    const processedUpdates = { ...designUpdates };
    if (designUpdates.colorTheme && designUpdates.colorTheme.colors) {
      // Convert full theme object to database format
      processedUpdates.colorTheme = designUpdates.colorTheme.colors;
      // Store theme name separately for frontend use
      processedUpdates.colorThemeName = designUpdates.colorTheme.name;
    }

    let updatedSite;

    // Check if we're in multi-page mode and have a current page
    if (currentSite.isMultiPage && currentPage && !currentPage.isDefault) {
      // Check if custom theme is enabled or if we're toggling the customThemeEnabled property
      const isTogglingCustomTheme = 'customThemeEnabled' in processedUpdates;
      const customThemeEnabled = currentPage.design?.customThemeEnabled !== false;
      
      if (customThemeEnabled || isTogglingCustomTheme) {
        // Update page-specific design
        const updatedPages = getCurrentPages().map(page => {
          if (page.id === currentPage.id) {
            return {
              ...page,
              design: {
                ...page.design,
                ...processedUpdates
              },
              updatedAt: new Date()
            };
          }
          return page;
        });

        updatedSite = {
          ...currentSite,
          pages: updatedPages
        };

        // Update current page reference
        const updatedCurrentPage = updatedPages.find(p => p.id === currentPage.id);
        setCurrentPage(updatedCurrentPage);
      } else {
        // Custom theme is disabled, don't allow theme updates (just return current site)
        updatedSite = currentSite;
        return; // Early return to prevent saving
      }
    } else {
      // Update global site design (for single-page sites or main page)
      updatedSite = { 
        ...currentSite, 
        design: {
          ...currentSite.design,
          ...processedUpdates
        }
      };
    }

    // Handle settings updates separately (always at site level)
    if (settings) {
      updatedSite = {
        ...updatedSite,
        settings: {
          ...currentSite.settings,
          ...settings
        }
      };
    }

    // Handle SEO updates separately (always at site level)
    if (seo) {
      updatedSite = {
        ...updatedSite,
        seo: {
          ...currentSite.seo,
          ...seo
        }
      };
    }
    
    // Update site context immediately for preview
    setCurrentSite(updatedSite);
    setUnsavedChanges(true);
    
    // Debounce the actual save to database
    debouncedSave(updatedSite);
  };

  // Custom theme management functions
  const handleCreateCustomTheme = () => {
    setEditingCustomTheme(null);
    setShowCustomThemeCreator(true);
  };

  const handleEditCustomTheme = (theme) => {
    setEditingCustomTheme(theme);
    setShowCustomThemeCreator(true);
  };

  const handleSaveCustomTheme = (themeData) => {
    if (!currentSite) return;

    const customThemes = currentSite.design?.customThemes || [];
    let updatedCustomThemes;

    if (editingCustomTheme) {
      // Update existing theme
      updatedCustomThemes = customThemes.map(theme => 
        theme.id === editingCustomTheme.id ? themeData : theme
      );
      toast.success(`Theme "${themeData.name}" updated!`);
    } else {
      // Add new theme
      updatedCustomThemes = [...customThemes, themeData];
      toast.success(`Theme "${themeData.name}" created!`);
    }

    // Update the site
    handleThemeUpdate({ customThemes: updatedCustomThemes });
    
    // Reset state
    setEditingCustomTheme(null);
    setShowCustomThemeCreator(false);
  };

  const handleDeleteCustomTheme = (themeId) => {
    if (!currentSite) return;

    const customThemes = currentSite.design?.customThemes || [];
    const themeToDelete = customThemes.find(theme => theme.id === themeId);
    
    if (!themeToDelete) return;

    const updatedCustomThemes = customThemes.filter(theme => theme.id !== themeId);
    handleThemeUpdate({ customThemes: updatedCustomThemes });
    
    toast.success(`Theme "${themeToDelete.name}" deleted`);
  };

  const handleFaviconUpload = async (file) => {
    if (!file) return;
    
    // Validate file type and size
    const validTypes = ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, ICO, or SVG file for favicon');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB
      toast.error('Favicon file size must be under 5MB');
      return;
    }

    setFaviconUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('customName', `favicon-${Date.now()}`);

      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      // Update site with favicon URL
      handleThemeUpdate({ 
        design: { 
          ...currentSite?.design, 
          favicon: result.url 
        } 
      });
      
      toast.success('Favicon uploaded successfully!');
      setShowEditModal(null);
      
    } catch (error) {
      console.error('Favicon upload error:', error);
      toast.error('Failed to upload favicon. Please try again.');
    } finally {
      setFaviconUploading(false);
    }
  };

  // Show loading if we're loading, don't have a site, or have the wrong site
  if (loading || !currentSite || (siteId && currentSite._id !== siteId)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            Loading editor...
            {currentSite && siteId && currentSite._id !== siteId && (
              <span className="block text-sm text-gray-400 mt-1">
                Switching from {currentSite.name} to new site...
              </span>
            )}
          </p>
        </div>
      </div>
    );
  }

  const deviceClasses = {
    desktop: 'w-full',
    tablet: 'w-[780px] mx-auto',
    mobile: 'w-[400px] mx-auto'
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Bar */}
      <header className="bg-white border-b flex items-center justify-between px-2 py-2">
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition md:hidden"
              aria-label="Open mobile menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="text-sm hidden md:inline font-semibold text-gray-900">{currentSite.name}</h1>
          {unsavedChanges && (
            <span className="text-sm text-amber-600">• Unsaved changes</span>
          )}
        </div>

        {/* Page Management Controls */}
        {shouldShowMultiPageUI && (
          <div className="flex items-center space-x-2">
            {/* Page Tabs */}
            <div className="flex border rounded-lg overflow-hidden bg-gray-50">
              {getCurrentPages().map((page) => (
                <button
                  key={page.id}
                  onClick={() => handlePageSwitch(page.id)}
                  className={`px-2 py-1 text-sm font-medium transition ${
                    currentPage?.id === page.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {page.name}
                </button>
              ))}
            </div>

            {/* Add Page Button */}
            <button
              onClick={() => setShowPageManager(true)}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              title="Manage pages"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex items-center space-x-1 md:space-x-2">
          {/* Device Preview Toggle */}
          <div className="flex border rounded-lg overflow-hidden hidden md:flex">
            <button
              onClick={() => setPreviewDevice('desktop')}
              className={`p-2 ${previewDevice === 'desktop' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewDevice('tablet')}
              className={`p-2 ${previewDevice === 'tablet' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewDevice('mobile')}
              className={`p-2 ${previewDevice === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={handleSave}
            disabled={!unsavedChanges || saving}
            className={`flex items-center px-2 py-2 rounded-lg text-sm transition ${
              saving
                ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                : unsavedChanges 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-green-100 text-green-800 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : unsavedChanges ? 'Save Now' : 'Saved'}
          </button>

          <button
            onClick={handlePublish}
            className="flex items-center px-2 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Publish
          </button>

          {currentSite.status === 'published' && currentSite.domain && (
            <a
              href={getPreviewUrl(currentSite.domain)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
            >
              <Eye className="w-4 h-4 mr-1" />
              View
            </a>
          )}
        </div>
      </header>

      {/* Transfer Restriction Banner */}
      {currentSite && (
        <TransferRestrictionBanner 
          site={currentSite} 
          editPermissions={currentSite.editPermissions} 
        />
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Editor Panels (Desktop) */}
        <div className={`${isMobile ? 'hidden' : 'w-72'} bg-white border-r flex flex-col`}>
          {/* Panel Navigation */}
          <div className="border-b">
            <nav className="flex">
              <button
                onClick={() => setActivePanel('sections')}
                className={`flex items-center px-1 py-2 text-sm font-medium border-b-2 transition ${
                  activePanel === 'sections'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Layout className="w-4 h-4 mr-2" />
                Sections
              </button>
              <button
                onClick={() => setActivePanel('theme')}
                className={`flex items-center px-1 py-2 text-sm font-medium border-b-2 transition ${
                  activePanel === 'theme'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Palette className="w-4 h-4 mr-2" />
                Theme
              </button>
              <button
                onClick={() => setActivePanel('settings')}
                className={`flex items-center px-1 py-2 text-sm font-medium border-b-2 transition ${
                  activePanel === 'settings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </button>
            </nav>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto">
            {activePanel === 'sections' && (
              <div className="p-4">
                {/* Sections Management */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {currentPage?.name || 'Page'} Sections
                  </h3>
                  <button 
                    onClick={() => {
                      const currentSectionCount = getCurrentPageSections().length;
                      const maxSections = planInfo?.limits?.sections?.max || 6;
                      
                      if (planInfo && maxSections !== 'Unlimited' && currentSectionCount >= maxSections) {
                        toast.error(`Your ${planInfo.plan.name} plan allows maximum ${maxSections} sections per page`);
                        return;
                      }
                      
                      setShowSectionChooser(true);
                    }}
                    className={`p-1 rounded transition ${
                      planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title={
                      planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max
                        ? 'Section limit reached for your plan'
                        : 'Add new section'
                    }
                    disabled={planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  {/* Section Selector */}
                  <div>
                    <select
                      value={selectedSection?.id || ''}
                      onChange={(e) => {
                        const section = getCurrentPageSections().find(s => s.id === e.target.value);
                        handleSectionSelect(section || null);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="">Choose a section...</option>
                      {getCurrentPageSections()
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((section) => (
                          <option key={section.id} value={section.id}>
                            {section.type.charAt(0).toUpperCase() + section.type.slice(1)} 
                            {section.visible ? ' ✓' : ' ✕'}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                </div>
              </div>
            )}

            {activePanel === 'theme' && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Theme</h3>
                  <button className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Custom Theme Toggle for Subpages */}
                {!isMainPage() && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={currentPage?.design?.customThemeEnabled !== false}
                        onChange={(e) => handleThemeUpdate({ customThemeEnabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        Enable custom theme for this page
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      When disabled, this page will inherit the main page's theme
                    </p>
                  </div>
                )}

                {/* Theme Controls - Only show if main page or custom theme is enabled */}
                {(isMainPage() || currentPage?.design?.customThemeEnabled !== false) && (
                  <>
                    {/* Colors Section */}
                    <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-medium text-gray-900">Colors</h4>
                    <button 
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className="text-purple-600 text-xs hover:text-purple-700"
                    >
                      Replace
                    </button>
                  </div>
                  
                  {/* Current Color Palette Display */}
                  {!showColorPicker && (
                    <div className="grid grid-cols-4 gap-1 mb-1">
                      <div 
                        className="rounded-sm p-0 flex items-center justify-center h-8"
                        style={{ backgroundColor: getCurrentTheme().colors.primary }}
                      >
                        <span className={`text-sm font-medium ${getCurrentTheme().colors.primary === '#FFFFFF' ? 'text-gray-900' : 'text-white'}`}>Aa</span>
                      </div>
                      <div 
                        className="rounded-sm p-0 flex items-center justify-center h-8"
                        style={{ backgroundColor: getCurrentTheme().colors.secondary }}
                      >
                        <span className={`text-sm font-medium ${getCurrentTheme().colors.secondary === '#FFFFFF' ? 'text-gray-900' : 'text-white'}`}>Aa</span>
                      </div>
                      <div 
                        className="rounded-sm p-0 flex items-center justify-center border h-8"
                        style={{ backgroundColor: getCurrentTheme().colors.background }}
                      >
                        <span style={{ color: getCurrentTheme().colors.text }} className="text-sm font-medium">Aa</span>
                      </div>
                      <div 
                        className="rounded-sm p-0 flex items-center justify-center h-8"
                        style={{ backgroundColor: getCurrentTheme().colors.accent }}
                      >
                        <span className={`text-sm font-medium ${['#FFFFFF', '#FFFBEB', '#FEFBFF', '#FEF2F2'].includes(getCurrentTheme().colors.accent) ? 'text-gray-900' : 'text-white'}`}>Aa</span>
                      </div>
                    </div>
                  )}

                  {/* Color Picker Grid */}
                  {showColorPicker && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {COLOR_THEMES.map((theme, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            // Update site theme
                            handleThemeUpdate({ colorTheme: theme });
                            setShowColorPicker(false);
                          }}
                          className={`p-1.5 border border-gray-200 rounded-md hover:border-purple-500 transition ${
                            getCurrentTheme().name === theme.name ? 'ring-2 ring-purple-500' : ''
                          }`}
                        >
                          <div className="space-y-1">
                            <div className="flex space-x-1">
                              <div 
                                className="w-2.5 h-2.5 rounded border border-gray-200" 
                                style={{ backgroundColor: theme.colors.primary }}
                              />
                              <div 
                                className="w-2.5 h-2.5 rounded border border-gray-200" 
                                style={{ backgroundColor: theme.colors.secondary }}
                              />
                              <div 
                                className="w-2.5 h-2.5 rounded border border-gray-200" 
                                style={{ backgroundColor: theme.colors.accent }}
                              />
                              <div 
                                className="w-2.5 h-2.5 rounded border border-gray-200" 
                                style={{ backgroundColor: theme.colors.background }}
                              />
                              <div 
                                className="w-2.5 h-2.5 rounded border border-gray-200" 
                                style={{ backgroundColor: theme.colors.text }}
                              />
                            </div>
                            <div className="text-xs font-medium text-gray-900 truncate">{theme.name}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Custom Themes Section */}
                  {showColorPicker && (
                    <div>
                    {currentSite?.design?.customThemes && currentSite.design.customThemes.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">🎨 Your Custom Themes</h5>
                        <div className="grid grid-cols-3 gap-2">
                          {currentSite.design.customThemes.map((theme, index) => (
                            <div key={theme.id} className="relative group">
                              <button
                                onClick={() => {
                                  handleThemeUpdate({ 
                                    colorTheme: { 
                                      name: theme.name, 
                                      colors: theme.colors 
                                    } 
                                  });
                                  setShowColorPicker(false);
                                }}
                                className={`p-1.5 border border-gray-200 rounded-md hover:border-purple-500 transition w-full ${
                                  getCurrentTheme().name === theme.name ? 'ring-2 ring-purple-500' : ''
                                }`}
                              >
                                <div className="space-y-1">
                                  <div className="flex space-x-1">
                                    <div 
                                      className="w-2.5 h-2.5 rounded border border-gray-200" 
                                      style={{ backgroundColor: theme.colors.primary }}
                                    />
                                    <div 
                                      className="w-2.5 h-2.5 rounded border border-gray-200" 
                                      style={{ backgroundColor: theme.colors.secondary }}
                                    />
                                    <div 
                                      className="w-2.5 h-2.5 rounded border border-gray-200" 
                                      style={{ backgroundColor: theme.colors.accent }}
                                    />
                                    <div 
                                      className="w-2.5 h-2.5 rounded border border-gray-200" 
                                      style={{ backgroundColor: theme.colors.background }}
                                    />
                                    <div 
                                      className="w-2.5 h-2.5 rounded border border-gray-200" 
                                      style={{ backgroundColor: theme.colors.text }}
                                    />
                                  </div>
                                  <div className="text-xs font-medium text-gray-900 truncate">{theme.name}</div>
                                </div>
                              </button>
                              
                              {/* Edit/Delete buttons */}
                              <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCustomTheme(theme);
                                  }}
                                  className="w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center hover:bg-blue-700 transition-colors"
                                  title="Edit theme"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Delete theme "${theme.name}"?`)) {
                                      handleDeleteCustomTheme(theme.id);
                                    }
                                  }}
                                  className="w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-700 transition-colors"
                                  title="Delete theme"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Create Custom Theme Button */}
                    <button
                      onClick={handleCreateCustomTheme}
                      className="w-full p-3 text-sm text-purple-600 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>🎨</span>
                      <span>Create Custom Theme</span>
                    </button>
                    </div>
                  )}
                </div>

                {/* Fonts Section */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-gray-900">Fonts</h4>
                    <button 
                      onClick={() => setShowFontPicker(!showFontPicker)}
                      className="text-purple-600 text-xs hover:text-purple-700"
                    >
                      Replace
                    </button>
                  </div>
                  
                  {!showFontPicker && (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                      <div className="text-xs font-semibold text-gray-900 mb-1" style={{ fontFamily: getCurrentDesign().fontFamily || 'Inter' }}>
                        {getCurrentDesign().fontFamily || 'Inter'}
                      </div>
                      <div className="text-xs text-gray-600">Primary font</div>
                    </div>
                  )}

                  {/* Font Picker */}
                  {showFontPicker && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {FONT_OPTIONS.map((font) => (
                        <button
                          key={font.name}
                          onClick={() => {
                            handleThemeUpdate({ fontFamily: font.name });
                            setShowFontPicker(false);
                          }}
                          className={`p-2 border border-gray-200 rounded-md hover:border-purple-500 transition text-left ${
                            (getCurrentDesign().fontFamily || 'Inter') === font.name ? 'ring-2 ring-purple-500' : ''
                          }`}
                        >
                          <div style={{ fontFamily: font.name }} className="text-xs font-medium text-gray-900">
                            {font.name}
                          </div>
                          <div className="text-xs text-gray-500">{font.label}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Heading Controls */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-900 mb-2">Heading size</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">T</span>
                    <div className="flex-1 relative">
                      <input 
                        type="range" 
                        min="12" 
                        max="48" 
                        value={getCurrentDesign().headingSize || 20}
                        onChange={(e) => handleThemeUpdate({ headingSize: parseInt(e.target.value) })}
                        className="w-full h-1.5 bg-gray-300 accent-gray-600 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-900">T</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-center">{getCurrentDesign().headingSize || 24}px</div>
                </div>

                {/* Heading Weight */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-900 mb-2">Heading weight</label>
                  <div className="flex items-center justify-between space-x-1">
                    {['light', 'regular', 'medium', 'semibold', 'bold'].map((weight) => (
                      <button
                        key={weight}
                        type="button"
                        onClick={() => handleThemeUpdate({ headingWeight: weight })}
                        className={`px-1.5 py-1 text-xs rounded-md flex-1 transition-colors ${getCurrentDesign().headingWeight === weight ? 
                          'bg-gray-800 text-white' : 
                          'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        <span className="capitalize text-xs">{weight}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text Size */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-900 mb-2">Text size</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">a</span>
                    <div className="flex-1 relative">
                      <input 
                        type="range" 
                        min="8" 
                        max="24" 
                        value={getCurrentDesign().textSize || 15}
                        onChange={(e) => handleThemeUpdate({ textSize: parseInt(e.target.value) })}
                        className="w-full h-1.5 bg-gray-300 accent-gray-600 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    <span className="text-base font-normal text-gray-900">a</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-center">{getCurrentDesign().textSize || 15}px</div>
                </div>

                {/* Image Corners */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-900 mb-2">Image corners</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      { value: 'square', label: 'Square', radius: '0px' },
                      { value: 'soft', label: 'Soft', radius: '4px' },
                      { value: 'round', label: 'Round', radius: '10px' },
                      { value: 'oval', label: 'Oval', radius: '28px' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleThemeUpdate({ imageCorners: option.value })}
                        className={`px-1.5 py-1 text-xs border transition-colors ${getCurrentDesign().imageCorners === option.value ? 
                          'bg-gray-800 text-white border-gray-800' : 
                          'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
                        style={{ borderRadius: option.radius }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Button Corners */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-900 mb-2">Button corners</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      { value: 'square', label: 'Square', radius: '0px' },
                      { value: 'soft', label: 'Soft', radius: '4px' },
                      { value: 'round', label: 'Round', radius: '10px' },
                      { value: 'oval', label: 'Oval', radius: '28px' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleThemeUpdate({ buttonCorners: option.value })}
                        className={`px-1.5 py-1 text-xs border transition-colors ${getCurrentDesign().buttonCorners === option.value ? 
                          'bg-gray-800 text-white border-gray-800' : 
                          'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
                        style={{ borderRadius: option.radius }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bottom Border */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900">Bottom border</div>
                    <div className="text-xs text-gray-500">A subtle horizontal line between sections</div>
                  </div>
                  <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                    <input 
                      type="checkbox" 
                      checked={getCurrentDesign().bottomBorder || false}
                      onChange={(e) => handleThemeUpdate({ bottomBorder: e.target.checked })}
                      className="sr-only" 
                    />
                    <div className={`block w-9 h-5 rounded-full transition ${
                      getCurrentDesign().bottomBorder ? 'bg-purple-600' : 'bg-gray-300'
                    }`}></div>
                    <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                      getCurrentDesign().bottomBorder ? 'right-0.5' : 'left-0.5'
                    }`}></div>
                  </label>
                </div>

                {/* Max Width */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-900 mb-2">Container width</label>
                  <div className="grid grid-cols-3 gap-1">
                    {[
                      { value: 'sm', label: 'XS', description: '20rem' },
                      { value: 'md', label: 'SM', description: '24rem' },
                      { value: '3xl', label: 'MD', description: '28rem' },
                      { value: '5xl', label: 'LG', description: '32rem' },
                      { value: '7xl', label: 'XL', description: '36rem' },
                      { value: 'full', label: 'Full', description: '100%' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleThemeUpdate({ maxWidth: option.value })}
                        className={`px-1 py-1.5 text-xs border transition-colors ${(getCurrentDesign().maxWidth || '3xl') === option.value ? 
                          'bg-gray-800 text-white border-gray-800' : 
                          'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
                        title={option.description}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-center">
                    {(() => {
                      const widthMap = { sm: '640px', md: '768px', lg: '1024px', '3xl': '1536px', '5xl': '1920px', '7xl': '2560px', full: '100%' };
                      return widthMap[getCurrentDesign().maxWidth || 'lg'];
                    })()}
                  </div>
                </div>

                {/* Dark/Light Mode */}
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900">Dark mode toggle</div>
                    <div className="text-xs text-gray-500">Add a dark/light switch at site header</div>
                  </div>
                  <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                    <input 
                      type="checkbox" 
                      checked={getCurrentDesign().darkMode || false}
                      onChange={(e) => handleThemeUpdate({ darkMode: e.target.checked })}
                      className="sr-only" 
                    />
                    <div className={`block w-9 h-5 rounded-full transition ${
                      getCurrentDesign().darkMode ? 'bg-purple-600' : 'bg-gray-300'
                    }`}></div>
                    <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                      getCurrentDesign().darkMode ? 'right-0.5' : 'left-0.5'
                    }`}></div>
                  </label>
                </div>
                  </>
                )}

                {/* Message when custom theme is disabled */}
                {!isMainPage() && currentPage?.design?.customThemeEnabled === false && (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a4 4 0 004-4V5z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      This page inherits the main page's theme
                    </p>
                    <p className="text-xs text-gray-400">
                      Enable custom theme above to customize this page's appearance
                    </p>
                  </div>
                )}
              </div>
            )}

            {activePanel === 'settings' && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Settings</h3>
                  <button className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Company Name */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Company name</div>
                      <div className="text-xs text-gray-500 truncate">{currentSite?.businessInfo?.name || 'Your Business'}</div>
                    </div>
                    <button 
                      onClick={() => {
                        setEditValue(currentSite?.businessInfo?.name || '');
                        setShowEditModal('companyName');
                      }}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      Edit
                    </button>
                  </div>

                  {/* Website Address */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Solo website address</div>
                      <div className="text-purple-600 text-xs truncate">https://webc.local/{currentSite?.name?.toLowerCase().replace(/\s+/g, '')}</div>
                      <div className="text-xs text-gray-500">Last published: {currentSite?.publishedAt ? new Date(currentSite.publishedAt).toLocaleDateString() : 'Never'}</div>
                    </div>
                    <button
                      onClick={() => {
                        setEditValue(currentSite?.name?.toLowerCase().replace(/\s+/g, '') || '');
                        setShowEditModal('websiteAddress');
                      }}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      Edit
                    </button>
                  </div>

                  {/* Website Settings Header */}
                  <div className="pt-3 border-t border-gray-200">
                    <h4 className="text-xs font-medium text-gray-900 mb-3">Website settings</h4>
                  </div>

                  {/* Custom Domain */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Custom domain</div>
                      <div className="text-xs text-gray-500">Point your domain to your Solo site, or buy one from our partner</div>
                    </div>
                    <button
                      onClick={() => setShowDomainManager(true)}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      Connect
                    </button>
                  </div>

                  {/* Google Analytics */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Google Analytics</div>
                      <div className="text-xs text-gray-500">Get performance statistics for your website</div>
                    </div>
                    <button className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0">Configure</button>
                  </div>

                  {/* Cookie Banner */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Cookie banner</div>
                      <div className="text-xs text-gray-500">Ask site visitors to accept a cookie use policy</div>
                    </div>
                    <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                      <input 
                        type="checkbox" 
                        checked={currentSite?.settings?.cookieBanner || false}
                        onChange={(e) => handleThemeUpdate({ settings: { ...currentSite?.settings, cookieBanner: e.target.checked } })}
                        className="sr-only" 
                      />
                      <div className={`block w-9 h-5 rounded-full transition ${
                        currentSite?.settings?.cookieBanner ? 'bg-purple-600' : 'bg-gray-300'
                      }`}></div>
                      <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                        currentSite?.settings?.cookieBanner ? 'right-0.5' : 'left-0.5'
                      }`}></div>
                    </label>
                  </div>

                  {/* Favicon */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Favicon</div>
                      <div className="text-xs text-gray-500">
                        {currentSite?.design?.favicon ? 
                          `Uploaded favicon: ${currentSite.design.favicon.split('/').pop()}` : 
                          'Upload a favicon for your website (recommended: 32x32 PNG or ICO file)'
                        }
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowEditModal('favicon')}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      {currentSite?.design?.favicon ? 'Change' : 'Upload'}
                    </button>
                  </div>

                  {/* Language Management */}
                  <div className="pt-3 border-t border-gray-200">
                    <LanguageManager 
                      site={currentSite} 
                      planInfo={planInfo}
                      onSiteUpdate={(updatedSite) => {
                        setCurrentSite(updatedSite);
                        setUnsavedChanges(false); // Language changes are saved automatically
                      }} 
                    />
                    
                    {/* Translation Manager Button */}
                    {currentSite?.languages && currentSite.languages.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => setShowTranslationManager(true)}
                          className="w-full flex items-center justify-center px-3 py-2 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Manage Translations
                        </button>
                      </div>
                    )}
                  </div>
                  {/* SEO Section */}
                  <div className="pt-3 border-t border-gray-200">
                    <h4 className="text-xs font-medium text-gray-900 mb-3">Search optimization (SEO)</h4>
                  </div>

                  {/* Page Title */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Page title</div>
                      <div className="text-xs text-gray-500 truncate">{currentSite?.seo?.title || `${currentSite?.businessInfo?.name} - Professional Services`}</div>
                    </div>
                    <button 
                      onClick={() => {
                        setEditValue(currentSite?.seo?.title || `${currentSite?.businessInfo?.name} - Professional Services`);
                        setShowEditModal('pageTitle');
                      }}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      Edit
                    </button>
                  </div>

                  {/* SEO Keywords */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">SEO keywords</div>
                      <div className="text-xs text-gray-500 truncate">{currentSite?.seo?.keywords?.join(', ') || 'business, services, professional'}</div>
                    </div>
                    <button 
                      onClick={() => {
                        setEditValue((currentSite?.seo?.keywords || []).join(', '));
                        setShowEditModal('seoKeywords');
                      }}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      Edit
                    </button>
                  </div>

                  {/* SEO Description */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Meta description</div>
                      <div className="text-xs text-gray-500 truncate">{currentSite?.seo?.description || 'Professional services tailored to your needs.'}</div>
                    </div>
                    <button 
                      onClick={() => {
                        setEditValue(currentSite?.seo?.description || 'Professional services tailored to your needs.');
                        setShowEditModal('seoDescription');
                      }}
                      className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                    >
                      Edit
                    </button>
                  </div>

                  {/* Search Indexing */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Search indexing</div>
                      <div className="text-xs text-gray-500">Make visible to search engines so customers can find you</div>
                    </div>
                    <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                      <input 
                        type="checkbox" 
                        checked={currentSite?.settings?.searchIndexing !== false}
                        onChange={(e) => handleThemeUpdate({ settings: { ...currentSite?.settings, searchIndexing: e.target.checked } })}
                        className="sr-only" 
                      />
                      <div className={`block w-9 h-5 rounded-full transition ${
                        currentSite?.settings?.searchIndexing !== false ? 'bg-purple-600' : 'bg-gray-300'
                      }`}></div>
                      <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                        currentSite?.settings?.searchIndexing !== false ? 'right-0.5' : 'left-0.5'
                      }`}></div>
                    </label>
                  </div>


                  {/* Export Contacts */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Export contacts</div>
                      <div className="text-xs text-gray-500">Download a file of newsletter and contact submissions</div>
                    </div>
                    <button className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0">Export Contacts</button>
                  </div>

                  {/* Custom Code */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Custom code</div>
                      <div className="text-xs text-gray-500">Add code to the &lt;head&gt; tag</div>
                      <div className="flex items-center text-xs text-amber-600 mt-1">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Custom domain required
                      </div>
                    </div>
                    <button className="text-gray-400 text-xs cursor-not-allowed ml-2 flex-shrink-0">Disabled</button>
                  </div>
                </div>
              </div>
            )}

            {/* Section Editor Panel */}
            {selectedSection && activePanel === 'sections' && (
              <SectionEditor
                section={selectedSection}
                site={currentSite}
                onUpdate={(updates) => handleSectionUpdate(selectedSection.id, updates)}
                onClose={() => handleSectionSelect(null)}
                canEditHeaderFooter={canEditHeaderFooter()}
                onCreateCustomTheme={handleCreateCustomTheme}
                isMainPage={isMainPage()}
              />
            )}
          </div>
        </div>

        {/* Main Preview Area */}
        <div className="flex-1 overflow-auto p-0 md:p-4 ">
          <div className={`${deviceClasses[previewDevice]} flex justify-center border-0 md:border-2 p-0 md:p-2 border-gray-300 rounded-lg transition-all duration-300 min-h-full`}>
            <LivePreview
              site={currentSite}
              currentPage={currentPage}
              sections={getCurrentPageSections()}
              selectedSection={selectedSection}
              onSectionSelect={handleSectionSelect}
              onSectionUpdate={handleSectionUpdate}
              onSectionMove={handleSectionMove}
              onSectionDelete={handleSectionDelete}
              onSectionRegenerate={handleSectionRegenerate}
              regeneratingSection={regeneratingSection}
            />
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobile && isMobileDrawerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={handleDrawerBackdropClick}
        >
          <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">Site Editor</h2>
              <button
                onClick={() => setIsMobileDrawerOpen(false)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel Navigation */}
            <div className="border-b">
              <nav className="flex">
                <button
                  onClick={() => setActivePanel('sections')}
                  className={`flex-1 flex items-center justify-center px-1 py-3 text-sm font-medium border-b-2 transition ${
                    activePanel === 'sections'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Layout className="w-4 h-4 mr-2" />
                  Sections
                </button>
                <button
                  onClick={() => setActivePanel('theme')}
                  className={`flex-1 flex items-center justify-center px-1 py-3 text-sm font-medium border-b-2 transition ${
                    activePanel === 'theme'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Palette className="w-4 h-4 mr-2" />
                  Theme
                </button>
                <button
                  onClick={() => setActivePanel('settings')}
                  className={`flex-1 flex items-center justify-center px-1 py-3 text-sm font-medium border-b-2 transition ${
                    activePanel === 'settings'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </button>
              </nav>
            </div>

            {/* Panel Content - Exact copy from desktop */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {activePanel === 'sections' && (
                <div className="p-4">
                  {/* Sections Management */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {currentPage?.name || 'Page'} Sections
                    </h3>
                    <button 
                      onClick={() => {
                        const currentSectionCount = getCurrentPageSections().length;
                        const maxSections = planInfo?.limits?.sections?.max || 6;
                        
                        if (planInfo && maxSections !== 'Unlimited' && currentSectionCount >= maxSections) {
                          toast.error(`Your ${planInfo.plan.name} plan allows maximum ${maxSections} sections per page`);
                          return;
                        }
                        
                        setShowSectionChooser(true);
                        setIsMobileDrawerOpen(false); // Close mobile drawer when opening modal
                      }}
                      className={`p-1 rounded transition ${
                        planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                      title={
                        planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max
                          ? 'Section limit reached for your plan'
                          : 'Add new section'
                      }
                      disabled={planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Section Selector */}
                    <div>
                      <select
                        value={selectedSection?.id || ''}
                        onChange={(e) => {
                          const section = getCurrentPageSections().find(s => s.id === e.target.value);
                          handleSectionSelect(section || null);
                          // Keep drawer open when selecting sections for better UX
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value="">Choose a section...</option>
                        {getCurrentPageSections()
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((section) => (
                            <option key={section.id} value={section.id}>
                              {section.type.charAt(0).toUpperCase() + section.type.slice(1)} 
                              {section.visible ? ' ✓' : ' ✕'}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activePanel === 'theme' && (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Theme</h3>
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Custom Theme Toggle for Subpages */}
                  {!isMainPage() && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={currentPage?.design?.customThemeEnabled !== false}
                          onChange={(e) => handleThemeUpdate({ customThemeEnabled: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          Enable custom theme for this page
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        When disabled, this page will inherit the main page's theme
                      </p>
                    </div>
                  )}

                  {/* Theme Controls - Only show if main page or custom theme is enabled */}
                  {(isMainPage() || currentPage?.design?.customThemeEnabled !== false) && (
                    <>
                      {/* Colors Section */}
                      <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-medium text-gray-900">Colors</h4>
                      <button 
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        className="text-purple-600 text-xs hover:text-purple-700"
                      >
                        Replace
                      </button>
                    </div>
                    
                    {/* Current Color Palette Display */}
                    {!showColorPicker && (
                      <div className="grid grid-cols-4 gap-1 mb-1">
                        <div 
                          className="rounded-sm p-0 flex items-center justify-center h-8"
                          style={{ backgroundColor: getCurrentTheme().colors.primary }}
                        >
                          <span className={`text-sm font-medium ${getCurrentTheme().colors.primary === '#FFFFFF' ? 'text-gray-900' : 'text-white'}`}>Aa</span>
                        </div>
                        <div 
                          className="rounded-sm p-0 flex items-center justify-center h-8"
                          style={{ backgroundColor: getCurrentTheme().colors.secondary }}
                        >
                          <span className={`text-sm font-medium ${getCurrentTheme().colors.secondary === '#FFFFFF' ? 'text-gray-900' : 'text-white'}`}>Aa</span>
                        </div>
                        <div 
                          className="rounded-sm p-0 flex items-center justify-center border h-8"
                          style={{ backgroundColor: getCurrentTheme().colors.background }}
                        >
                          <span style={{ color: getCurrentTheme().colors.text }} className="text-sm font-medium">Aa</span>
                        </div>
                        <div 
                          className="rounded-sm p-0 flex items-center justify-center h-8"
                          style={{ backgroundColor: getCurrentTheme().colors.accent }}
                        >
                          <span className={`text-sm font-medium ${['#FFFFFF', '#FFFBEB', '#FEFBFF', '#FEF2F2'].includes(getCurrentTheme().colors.accent) ? 'text-gray-900' : 'text-white'}`}>Aa</span>
                        </div>
                      </div>
                    )}

                    {/* Color Picker Grid */}
                    {showColorPicker && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {COLOR_THEMES.map((theme, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              // Update site theme
                              handleThemeUpdate({ colorTheme: theme });
                              setShowColorPicker(false);
                            }}
                            className={`p-1.5 border border-gray-200 rounded-md hover:border-purple-500 transition ${
                              getCurrentTheme().name === theme.name ? 'ring-2 ring-purple-500' : ''
                            }`}
                          >
                            <div className="space-y-1">
                              <div className="flex space-x-1">
                                <div 
                                  className="w-2.5 h-2.5 rounded border border-gray-200" 
                                  style={{ backgroundColor: theme.colors.primary }}
                                />
                                <div 
                                  className="w-2.5 h-2.5 rounded border border-gray-200" 
                                  style={{ backgroundColor: theme.colors.secondary }}
                                />
                                <div 
                                  className="w-2.5 h-2.5 rounded border border-gray-200" 
                                  style={{ backgroundColor: theme.colors.accent }}
                                />
                                <div 
                                  className="w-2.5 h-2.5 rounded border border-gray-200" 
                                  style={{ backgroundColor: theme.colors.background }}
                                />
                                <div 
                                  className="w-2.5 h-2.5 rounded border border-gray-200" 
                                  style={{ backgroundColor: theme.colors.text }}
                                />
                              </div>
                              <div className="text-xs font-medium text-gray-900 truncate">{theme.name}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Fonts Section */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-medium text-gray-900">Fonts</h4>
                      <button 
                        onClick={() => setShowFontPicker(!showFontPicker)}
                        className="text-purple-600 text-xs hover:text-purple-700"
                      >
                        Replace
                      </button>
                    </div>
                    
                    {!showFontPicker && (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                        <div className="text-xs font-semibold text-gray-900 mb-1" style={{ fontFamily: getCurrentDesign().fontFamily || 'Inter' }}>
                          {getCurrentDesign().fontFamily || 'Inter'}
                        </div>
                        <div className="text-xs text-gray-600">Primary font</div>
                      </div>
                    )}

                    {/* Font Picker */}
                    {showFontPicker && (
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {FONT_OPTIONS.map((font) => (
                          <button
                            key={font.name}
                            onClick={() => {
                              handleThemeUpdate({ fontFamily: font.name });
                              setShowFontPicker(false);
                            }}
                            className={`p-2 border border-gray-200 rounded-md hover:border-purple-500 transition text-left ${
                              (getCurrentDesign().fontFamily || 'Inter') === font.name ? 'ring-2 ring-purple-500' : ''
                            }`}
                          >
                            <div style={{ fontFamily: font.name }} className="text-xs font-medium text-gray-900">
                              {font.name}
                            </div>
                            <div className="text-xs text-gray-500">{font.label}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Heading Controls */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-900 mb-2">Heading size</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">T</span>
                      <div className="flex-1 relative">
                        <input 
                          type="range" 
                          min="12" 
                          max="48" 
                          value={getCurrentDesign().headingSize || 20}
                          onChange={(e) => handleThemeUpdate({ headingSize: parseInt(e.target.value) })}
                          className="w-full h-1.5 bg-gray-300 accent-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <span className="text-sm font-bold text-gray-900">T</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-center">{getCurrentDesign().headingSize || 24}px</div>
                  </div>

                  {/* Heading Weight */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-900 mb-2">Heading weight</label>
                    <div className="flex items-center justify-between space-x-1">
                      {['light', 'regular', 'medium', 'semibold', 'bold'].map((weight) => (
                        <button
                          key={weight}
                          type="button"
                          onClick={() => handleThemeUpdate({ headingWeight: weight })}
                          className={`px-1.5 py-1 text-xs rounded-md flex-1 transition-colors ${getCurrentDesign().headingWeight === weight ? 
                            'bg-gray-800 text-white' : 
                            'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          <span className="capitalize text-xs">{weight}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Text Size */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-900 mb-2">Text size</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">a</span>
                      <div className="flex-1 relative">
                        <input 
                          type="range" 
                          min="8" 
                          max="24" 
                          value={getCurrentDesign().textSize || 15}
                          onChange={(e) => handleThemeUpdate({ textSize: parseInt(e.target.value) })}
                          className="w-full h-1.5 bg-gray-300 accent-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <span className="text-base font-normal text-gray-900">a</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-center">{getCurrentDesign().textSize || 15}px</div>
                  </div>

                  {/* Image Corners */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-900 mb-2">Image corners</label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'square', label: 'Square', radius: '0px' },
                        { value: 'soft', label: 'Soft', radius: '4px' },
                        { value: 'round', label: 'Round', radius: '10px' },
                        { value: 'oval', label: 'Oval', radius: '28px' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleThemeUpdate({ imageCorners: option.value })}
                          className={`px-1.5 py-1 text-xs border transition-colors ${getCurrentDesign().imageCorners === option.value ? 
                            'bg-gray-800 text-white border-gray-800' : 
                            'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
                          style={{ borderRadius: option.radius }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Button Corners */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-900 mb-2">Button corners</label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'square', label: 'Square', radius: '0px' },
                        { value: 'soft', label: 'Soft', radius: '4px' },
                        { value: 'round', label: 'Round', radius: '10px' },
                        { value: 'oval', label: 'Oval', radius: '28px' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleThemeUpdate({ buttonCorners: option.value })}
                          className={`px-1.5 py-1 text-xs border transition-colors ${getCurrentDesign().buttonCorners === option.value ? 
                            'bg-gray-800 text-white border-gray-800' : 
                            'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
                          style={{ borderRadius: option.radius }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Bottom Border */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Bottom border</div>
                      <div className="text-xs text-gray-500">A subtle horizontal line between sections</div>
                    </div>
                    <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                      <input 
                        type="checkbox" 
                        checked={getCurrentDesign().bottomBorder || false}
                        onChange={(e) => handleThemeUpdate({ bottomBorder: e.target.checked })}
                        className="sr-only" 
                      />
                      <div className={`block w-9 h-5 rounded-full transition ${
                        getCurrentDesign().bottomBorder ? 'bg-purple-600' : 'bg-gray-300'
                      }`}></div>
                      <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                        getCurrentDesign().bottomBorder ? 'right-0.5' : 'left-0.5'
                      }`}></div>
                    </label>
                  </div>

                  {/* Max Width */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-900 mb-2">Container width</label>
                    <div className="grid grid-cols-3 gap-1">
                      {[
                        { value: 'sm', label: 'XS', description: '20rem' },
                        { value: 'md', label: 'SM', description: '24rem' },
                        { value: '3xl', label: 'MD', description: '28rem' },
                        { value: '5xl', label: 'LG', description: '32rem' },
                        { value: '7xl', label: 'XL', description: '36rem' },
                        { value: 'full', label: 'Full', description: '100%' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleThemeUpdate({ maxWidth: option.value })}
                          className={`px-1 py-1.5 text-xs border transition-colors ${(getCurrentDesign().maxWidth || '3xl') === option.value ? 
                            'bg-gray-800 text-white border-gray-800' : 
                            'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
                          title={option.description}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-center">
                      {(() => {
                        const widthMap = { sm: '640px', md: '768px', lg: '1024px', '3xl': '1536px', '5xl': '1920px', '7xl': '2560px', full: '100%' };
                        return widthMap[getCurrentDesign().maxWidth || 'lg'];
                      })()}
                    </div>
                  </div>

                  {/* Dark/Light Mode */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900">Dark mode toggle</div>
                      <div className="text-xs text-gray-500">Add a dark/light switch at site header</div>
                    </div>
                    <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                      <input 
                        type="checkbox" 
                        checked={getCurrentDesign().darkMode || false}
                        onChange={(e) => handleThemeUpdate({ darkMode: e.target.checked })}
                        className="sr-only" 
                      />
                      <div className={`block w-9 h-5 rounded-full transition ${
                        getCurrentDesign().darkMode ? 'bg-purple-600' : 'bg-gray-300'
                      }`}></div>
                      <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                        getCurrentDesign().darkMode ? 'right-0.5' : 'left-0.5'
                      }`}></div>
                    </label>
                  </div>
                    </>
                  )}

                  {/* Message when custom theme is disabled */}
                  {!isMainPage() && currentPage?.design?.customThemeEnabled === false && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a4 4 0 004-4V5z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        This page inherits the main page's theme
                      </p>
                      <p className="text-xs text-gray-400">
                        Enable custom theme above to customize this page's appearance
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activePanel === 'settings' && (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Settings</h3>
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Company Name */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Company name</div>
                        <div className="text-xs text-gray-500 truncate">{currentSite?.businessInfo?.name || 'Your Business'}</div>
                      </div>
                      <button 
                        onClick={() => {
                          setEditValue(currentSite?.businessInfo?.name || '');
                          setShowEditModal('companyName');
                          if (isMobile) setIsMobileDrawerOpen(false); // Close drawer when opening modal
                        }}
                        className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Website Address */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Solo website address</div>
                        <div className="text-purple-600 text-xs truncate">https://webc.local/{currentSite?.name?.toLowerCase().replace(/\s+/g, '')}</div>
                        <div className="text-xs text-gray-500">Last published: {currentSite?.publishedAt ? new Date(currentSite.publishedAt).toLocaleDateString() : 'Never'}</div>
                      </div>
                      <button
                        onClick={() => {
                          setEditValue(currentSite?.name?.toLowerCase().replace(/\s+/g, '') || '');
                          setShowEditModal('websiteAddress');
                          if (isMobile) setIsMobileDrawerOpen(false);
                        }}
                        className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Website Settings Header */}
                    <div className="pt-3 border-t border-gray-200">
                      <h4 className="text-xs font-medium text-gray-900 mb-3">Website settings</h4>
                    </div>

                    {/* Custom Domain */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Custom domain</div>
                        <div className="text-xs text-gray-500">Point your domain to your Solo site, or buy one from our partner</div>
                      </div>
                      <button
                        onClick={() => {
                          setShowDomainManager(true);
                          if (isMobile) setIsMobileDrawerOpen(false);
                        }}
                        className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                      >
                        Connect
                      </button>
                    </div>

                    {/* Google Analytics */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Google Analytics</div>
                        <div className="text-xs text-gray-500">Get performance statistics for your website</div>
                      </div>
                      <button className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0">Configure</button>
                    </div>

                    {/* Cookie Banner */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Cookie banner</div>
                        <div className="text-xs text-gray-500">Ask site visitors to accept a cookie use policy</div>
                      </div>
                      <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                        <input 
                          type="checkbox" 
                          checked={currentSite?.settings?.cookieBanner || false}
                          onChange={(e) => handleThemeUpdate({ settings: { ...currentSite?.settings, cookieBanner: e.target.checked } })}
                          className="sr-only" 
                        />
                        <div className={`block w-9 h-5 rounded-full transition ${
                          currentSite?.settings?.cookieBanner ? 'bg-purple-600' : 'bg-gray-300'
                        }`}></div>
                        <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                          currentSite?.settings?.cookieBanner ? 'right-0.5' : 'left-0.5'
                        }`}></div>
                      </label>
                    </div>

                    {/* Favicon */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Favicon</div>
                        <div className="text-xs text-gray-500">
                          {currentSite?.design?.favicon ? 
                            `Uploaded favicon: ${currentSite.design.favicon.split('/').pop()}` : 
                            'Upload a favicon for your website (recommended: 32x32 PNG or ICO file)'
                          }
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          setShowEditModal('favicon');
                          if (isMobile) setIsMobileDrawerOpen(false); // Close drawer when opening modal
                        }}
                        className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                      >
                        {currentSite?.design?.favicon ? 'Change' : 'Upload'}
                      </button>
                    </div>

                    {/* Language Management */}
                    <div className="pt-3 border-t border-gray-200">
                      <LanguageManager 
                        site={currentSite} 
                        planInfo={planInfo}
                        onSiteUpdate={(updatedSite) => {
                          setCurrentSite(updatedSite);
                          setUnsavedChanges(false); // Language changes are saved automatically
                        }} 
                      />
                      
                      {/* Translation Manager Button */}
                      {currentSite?.languages && currentSite.languages.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setShowTranslationManager(true);
                              if (isMobile) setIsMobileDrawerOpen(false);
                            }}
                            className="w-full flex items-center justify-center px-3 py-2 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Manage Translations
                          </button>
                        </div>
                      )}
                    </div>
                    {/* SEO Section */}
                    <div className="pt-3 border-t border-gray-200">
                      <h4 className="text-xs font-medium text-gray-900 mb-3">Search optimization (SEO)</h4>
                    </div>

                    {/* Page Title */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Page title</div>
                        <div className="text-xs text-gray-500 truncate">{currentSite?.seo?.title || `${currentSite?.businessInfo?.name} - Professional Services`}</div>
                      </div>
                      <button 
                        onClick={() => {
                          setEditValue(currentSite?.seo?.title || `${currentSite?.businessInfo?.name} - Professional Services`);
                          setShowEditModal('pageTitle');
                          if (isMobile) setIsMobileDrawerOpen(false); // Close drawer when opening modal
                        }}
                        className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                      >
                        Edit
                      </button>
                    </div>

                    {/* SEO Keywords */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">SEO keywords</div>
                        <div className="text-xs text-gray-500 truncate">{currentSite?.seo?.keywords?.join(', ') || 'business, services, professional'}</div>
                      </div>
                      <button 
                        onClick={() => {
                          setEditValue((currentSite?.seo?.keywords || []).join(', '));
                          setShowEditModal('seoKeywords');
                          if (isMobile) setIsMobileDrawerOpen(false); // Close drawer when opening modal
                        }}
                        className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Search Indexing */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Search indexing</div>
                        <div className="text-xs text-gray-500">Make visible to search engines so customers can find you</div>
                      </div>
                      <label className="relative inline-block w-9 h-5 cursor-pointer ml-2 flex-shrink-0">
                        <input 
                          type="checkbox" 
                          checked={currentSite?.settings?.searchIndexing !== false}
                          onChange={(e) => handleThemeUpdate({ settings: { ...currentSite?.settings, searchIndexing: e.target.checked } })}
                          className="sr-only" 
                        />
                        <div className={`block w-9 h-5 rounded-full transition ${
                          currentSite?.settings?.searchIndexing !== false ? 'bg-purple-600' : 'bg-gray-300'
                        }`}></div>
                        <div className={`dot absolute top-0.5 bg-white w-4 h-4 rounded-full transition ${
                          currentSite?.settings?.searchIndexing !== false ? 'right-0.5' : 'left-0.5'
                        }`}></div>
                      </label>
                    </div>

                    {/* Browser Icon */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Browser icon</div>
                        <div className="text-xs text-gray-500">A square icon displayed in browser tabs</div>
                      </div>
                      <button className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0">Upload</button>
                    </div>

                    {/* Export Contacts */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Export contacts</div>
                        <div className="text-xs text-gray-500">Download a file of newsletter and contact submissions</div>
                      </div>
                      <button className="text-purple-600 text-xs hover:text-purple-700 ml-2 flex-shrink-0">Export Contacts</button>
                    </div>

                    {/* Custom Code */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900">Custom code</div>
                        <div className="text-xs text-gray-500">Add code to the &lt;head&gt; tag</div>
                        <div className="flex items-center text-xs text-amber-600 mt-1">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Custom domain required
                        </div>
                      </div>
                      <button className="text-gray-400 text-xs cursor-not-allowed ml-2 flex-shrink-0">Disabled</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Section Editor Panel */}
              {selectedSection && activePanel === 'sections' && (
                <SectionEditor
                  section={selectedSection}
                  site={currentSite}
                  onUpdate={(updatedSection) => handleSectionUpdate(selectedSection.id, updatedSection)}
                  onClose={() => setSelectedSection(null)}
                  onRegenerate={() => handleSectionRegenerate(selectedSection.id)}
                  onCreateCustomTheme={handleCreateCustomTheme}
                  regenerating={regeneratingSection === selectedSection.id}
                  regenerationStep={regenerationStep}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section Chooser Modal */}
      {showSectionChooser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px] max-w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add New Section</h3>
                {planInfo && (
                  <div className="flex items-center space-x-2 mt-1">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-500">
                      {planInfo.plan.name}: {getCurrentPageSections().length}/{planInfo.limits.sections.max} sections
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowSectionChooser(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Choose a section type to add to your website. It will be inserted before the footer.
            </p>
            
            {planInfo && planInfo.limits.sections.max !== 'Unlimited' && getCurrentPageSections().length >= planInfo.limits.sections.max && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-sm text-red-800">
                    You've reached your plan limit of {planInfo.limits.sections.max} sections per page.
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_SECTIONS.map((section) => {
                // Check if this section type already exists
                // Allow multiple instances of specific section types
                const allowMultiple = ['article', 'custom-code', 'banner', 'iframe'].includes(section.type);
                const alreadyExists = !allowMultiple && getCurrentPageSections()?.some(s => s.type === section.type);
                
                // Check plan limits
                const currentSectionCount = getCurrentPageSections().length;
                const maxSections = planInfo?.limits?.sections?.max || 6;
                const atLimit = planInfo && maxSections !== 'Unlimited' && currentSectionCount >= maxSections;
                
                const isDisabled = alreadyExists || atLimit;
                
                return (
                  <button
                    key={section.type}
                    onClick={() => !isDisabled && handleSectionAdd(section.type)}
                    disabled={isDisabled}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      isDisabled
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                        {section.name}
                      </h4>
                      {alreadyExists && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                          Added
                        </span>
                      )}
                      {atLimit && !alreadyExists && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                          Plan Limit
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                      {section.description}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  AI will generate content based on your business information
                </p>
                <button
                  onClick={() => setShowSectionChooser(false)}
                  className="px-2 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {showEditModal === 'companyName' && 'Edit Company Name'}
                {showEditModal === 'pageTitle' && 'Edit Page Title'}
                {showEditModal === 'seoKeywords' && 'Edit SEO Keywords'}
                {showEditModal === 'seoDescription' && 'Edit Meta Description'}
                {showEditModal === 'favicon' && 'Upload Favicon'}
                {showEditModal === 'websiteAddress' && 'Edit Website Address'}
              </h3>
              <button
                onClick={() => setShowEditModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              {showEditModal === 'favicon' ? (
                <>
                  <div className="text-center">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <input
                        id="faviconInput"
                        type="file"
                        accept=".png,.ico,.svg,image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleFaviconUpload(e.target.files[0]);
                          }
                        }}
                        className="hidden"
                        disabled={faviconUploading}
                      />
                      <div className="space-y-3">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <button
                            type="button"
                            onClick={() => document.getElementById('faviconInput').click()}
                            disabled={faviconUploading}
                            className="text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                          >
                            {faviconUploading ? 'Uploading...' : 'Choose favicon file'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, ICO, or SVG up to 5MB<br/>
                          Recommended: 32x32 or 48x48 pixels
                        </p>
                      </div>
                    </div>
                    {currentSite?.design?.favicon && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">Current favicon:</p>
                        <div className="flex items-center justify-center mt-2">
                          <img 
                            src={currentSite.design.favicon} 
                            alt="Current favicon" 
                            className="w-8 h-8"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <span className="ml-2 text-xs text-gray-500">
                            {currentSite.design.favicon.split('/').pop()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : showEditModal === 'seoKeywords' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keywords (comma-separated)
                  </label>
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows={3}
                    placeholder="business, services, professional"
                  />
                </>
              ) : showEditModal === 'seoDescription' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description
                  </label>
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows={3}
                    placeholder="Professional services tailored to your needs."
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-500 mt-1">{editValue.length}/160 characters</p>
                </>
              ) : showEditModal === 'websiteAddress' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website Slug
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 text-sm mr-1">https://webc.local/</span>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="your-business"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Only lowercase letters, numbers, and hyphens allowed.</p>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {showEditModal === 'companyName' ? 'Company Name' : 'Page Title'}
                  </label>
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder={showEditModal === 'companyName' ? 'Your Business Name' : 'Your Page Title'}
                  />
                </>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {showEditModal === 'favicon' ? 'Close' : 'Cancel'}
              </button>
              {showEditModal !== 'favicon' && (
                <button
                onClick={() => {
                  if (showEditModal === 'companyName') {
                    handleThemeUpdate({ 
                      businessInfo: { 
                        ...currentSite?.businessInfo, 
                        name: editValue 
                      } 
                    });
                  } else if (showEditModal === 'pageTitle') {
                    handleThemeUpdate({ 
                      seo: { 
                        ...currentSite?.seo, 
                        title: editValue 
                      } 
                    });
                  } else if (showEditModal === 'seoKeywords') {
                    const keywords = editValue.split(',').map(k => k.trim()).filter(k => k);
                    handleThemeUpdate({ 
                      seo: { 
                        ...currentSite?.seo, 
                        keywords 
                      } 
                    });
                  } else if (showEditModal === 'seoDescription') {
                    handleThemeUpdate({
                      seo: {
                        ...currentSite?.seo,
                        description: editValue
                      }
                    });
                  } else if (showEditModal === 'websiteAddress') {
                    handleThemeUpdate({
                      slug: editValue
                    });
                    toast.success('Website address updated! Republish to apply changes.');
                  }
                  setShowEditModal(null);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Save
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fancy Regeneration Modal */}
      {regeneratingSection && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-96 max-w-full mx-4 relative overflow-hidden">
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 animate-pulse"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-6">
              <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-white animate-pulse" />
                  </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Designer at Work</h3>
                <p className="text-gray-600 text-sm">Creating something amazing for you...</p>
              </div>

              {/* Progress Steps */}
              <div className="space-y-3">
                {[
                  'Analyzing your business concept...',
                  'Consulting our AI design experts...',
                  'Crafting compelling content...',
                  'Sourcing professional imagery...',
                  'Optimizing visual hierarchy...',
                  'Finalizing your masterpiece...'
                ].map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center space-x-3 transition-all duration-500 ${
                      index <= regenerationStep ? 'opacity-100' : 'opacity-30'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full transition-all duration-500 ${
                      index < regenerationStep 
                        ? 'bg-green-500' 
                        : index === regenerationStep 
                        ? 'bg-blue-500 animate-pulse' 
                        : 'bg-gray-300'
                    }`}>
                      {index < regenerationStep && (
                        <div className="w-full h-full flex items-center justify-center text-white text-xs">
                          ✓
                        </div>
                      )}
                    </div>
                    <span className={`text-sm transition-all duration-2000 ${
                      index <= regenerationStep ? 'text-gray-900 font-medium' : 'text-gray-500'
                    }`}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>

              {/* Rotating progress indicator */}
              <div className="mt-6 flex items-center justify-center">
                <div className="relative">
                  {/* Outer spinning ring */}
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                  <div 
                    className="absolute inset-0 border-4 border-transparent border-t-purple-600 border-r-blue-600 rounded-full animate-spin"
                    style={{ animationDuration: '1.5s' }}
                  ></div>
                  
                  {/* Inner pulsing dot */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
              
              <p className="text-center text-xs text-gray-500 mt-3">
                AI is working its magic...
              </p>

              {/* Inspirational messages */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 italic">
                  {regenerationStep < 2 && "✨ Every pixel tells a story..."}
                  {regenerationStep >= 2 && regenerationStep < 4 && "🎨 Painting with words and colors..."}
                  {regenerationStep >= 4 && "🚀 Almost ready to amaze you!"}
                </p>
              </div>

              {/* Floating particles animation */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 bg-purple-400 rounded-full opacity-30 animate-bounce"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${i * 0.5}s`,
                      animationDuration: `${2 + Math.random() * 2}s`
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Addition Progress Modal */}
      {addingSection && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-96 max-w-full mx-4 relative overflow-hidden">
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 animate-pulse"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                  <Plus className="w-10 h-10 text-white animate-pulse" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Creating Your Section</h3>
                <p className="text-gray-600 text-sm">AI is building your {addingSection} section...</p>
              </div>

              {/* Progress Steps */}
              <div className="space-y-3">
                {[
                  `Analyzing your ${addingSection} requirements...`,
                  'Consulting our AI design experts...',
                  'Crafting compelling content...',
                  'Sourcing professional imagery...',
                  'Optimizing layout and structure...',
                  'Finalizing your new section...'
                ].map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center space-x-3 transition-all duration-500 ${
                      index <= addingSectionStep ? 'opacity-100' : 'opacity-30'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full transition-all duration-500 ${
                      index < addingSectionStep 
                        ? 'bg-green-500' 
                        : index === addingSectionStep 
                        ? 'bg-blue-500 animate-pulse' 
                        : 'bg-gray-300'
                    }`}>
                      {index < addingSectionStep && (
                        <div className="w-full h-full flex items-center justify-center text-white text-xs">
                          ✓
                        </div>
                      )}
                    </div>
                    <span className={`text-sm transition-all duration-500 ${
                      index <= addingSectionStep ? 'text-gray-900 font-medium' : 'text-gray-500'
                    }`}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>

              {/* Rotating progress indicator */}
              <div className="mt-6 flex items-center justify-center">
                <div className="relative">
                  {/* Outer spinning ring */}
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                  <div 
                    className="absolute inset-0 border-4 border-transparent border-t-green-600 border-r-blue-600 rounded-full animate-spin"
                    style={{ animationDuration: '1.5s' }}
                  ></div>
                  
                  {/* Inner pulsing dot */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 bg-gradient-to-r from-green-600 to-blue-600 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
              
              <p className="text-center text-xs text-gray-500 mt-3">
                Creating your perfect section...
              </p>

              {/* Inspirational messages */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 italic">
                  {addingSectionStep < 2 && "✨ Building something amazing..."}
                  {addingSectionStep >= 2 && addingSectionStep < 4 && "🎨 Crafting the perfect content..."}
                  {addingSectionStep >= 4 && "🚀 Almost ready to reveal!"}
                </p>
              </div>

              {/* Floating particles animation */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 bg-green-400 rounded-full opacity-30 animate-bounce"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${i * 0.5}s`,
                      animationDuration: `${2 + Math.random() * 2}s`
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Page Manager Modal */}
      {showPageManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px] max-w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Manage Pages</h3>
              <button
                onClick={() => setShowPageManager(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Add New Page */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Page</h4>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                  placeholder="Page name (e.g., About Us, Services)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newPageName.trim()) {
                      handlePageAdd(newPageName);
                    }
                  }}
                />
                <button
                  onClick={() => handlePageAdd(newPageName)}
                  disabled={!newPageName.trim() || addingPage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {addingPage ? 'Adding...' : 'Add Page'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                URL will be: {currentSite?.domain}.preview.sayfai.com/{generatePageSlug(newPageName || 'page-name')}
              </p>
            </div>

            {/* Existing Pages */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Current Pages ({getCurrentPages().length})
              </h4>
              
              <div className="space-y-2">
                {getCurrentPages().map((page) => (
                  <div
                    key={page.id}
                    className={`p-3 border rounded-lg ${
                      currentPage?.id === page.id ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{page.name}</span>
                          {page.isDefault && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          URL: {currentSite?.domain}.preview.sayfai.com/{page.slug === 'index' ? '' : page.slug}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {page.sections?.length || 0} sections
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageSwitch(page.id)}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          Edit
                        </button>
                        {!page.isDefault && (
                          <button
                            onClick={() => {
                              setConfirmationModal({
                                title: 'Delete Page',
                                message: `Delete page "${page.name}"? This cannot be undone.`,
                                onConfirm: () => {
                                  handlePageDelete(page.id);
                                  setConfirmationModal(null);
                                },
                                onCancel: () => setConfirmationModal(null)
                              });
                            }}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-gray-500">
                    Multi-page websites help organize content and improve SEO
                  </p>
                  <button
                    onClick={fixAllPagesSections}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    🔧 Fix missing header/footer on all pages
                  </button>
                </div>
                <button
                  onClick={() => setShowPageManager(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {confirmationModal.title}
            </h3>
            <p className="text-gray-600 mb-6">
              {confirmationModal.message}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={confirmationModal.onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmationModal.onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publishing Modal */}
      <PublishingModal
        isOpen={showPublishingModal}
        onClose={() => setShowPublishingModal(false)}
        onPublish={performPublish}
        site={currentSite}
      />

      {/* Custom Theme Creator Modal */}
      <CustomThemeCreator
        isOpen={showCustomThemeCreator}
        onClose={() => {
          setShowCustomThemeCreator(false);
          setEditingCustomTheme(null);
        }}
        onSave={handleSaveCustomTheme}
        editingTheme={editingCustomTheme}
      />

      {/* Translation Manager Modal */}
      {showTranslationManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Translation Manager</h2>
              <button
                onClick={() => setShowTranslationManager(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="p-6">
                <TranslationManager
                  site={currentSite}
                  onSiteUpdate={(updatedSite) => {
                    setCurrentSite(updatedSite);
                    setUnsavedChanges(false);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Domain Manager Modal */}
      {showDomainManager && (
        <DomainManager
          siteId={currentSite?._id}
          siteName={currentSite?.name}
          onClose={() => setShowDomainManager(false)}
        />
      )}
    </div>
  );
};

export default SiteEditor;